import { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import cx from 'classnames'
import { useQueryClient } from 'react-query'
import Styles from './V4BrandMaterials.module.scss'
import { ACCEPTED_BRAND_MATERIALS_MIME_TYPES, splitFilesByDestination } from './uploadRouting'
import { AdjustMediaModal } from './AdjustMediaModal/AdjustMediaModal'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Text } from 'blaze-ui/Text/Text'
import { Globe, Upload5, Upload } from 'blaze-ui/icons/20'
import { Trash2, File } from 'blaze-ui/icons'
import { Pdf, Markdown, TextFile, Video } from 'blaze-ui/icons/24'
import { useInfiniteBrandKitMediaFiles, IMediaFile } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useModals } from 'almanac-ui/Modal'
import { collectResults } from 'utils/query'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import type { SourceMaterialsMediaObject, BrandKitSource } from 'types'

const MAX_SOURCE_MATERIAL_FILES = 20

type SourceMaterialDoc = BrandKitSource<SourceMaterialsMediaObject>

type UploadedRow =
  | { kind: 'source'; id: number; filename: string; mimeType?: string; sourceUrl?: string }
  | { kind: 'media'; id: number; filename: string; mimeType: string; sourceUrl: string; thumbnailUrl?: string }

const isImageMime = (mime?: string) => !!mime && mime.startsWith('image/')
const isVideoMime = (mime?: string) => !!mime && mime.startsWith('video/')

const extractFilename = (url?: string) => {
  if (!url) return 'File'
  try {
    const pathname = new URL(url).pathname
    return decodeURIComponent(pathname.split('/').filter(Boolean).pop() ?? '') || 'File'
  } catch {
    return 'File'
  }
}

const getRowIcon = (mimeType?: string, sourceUrl?: string) => {
  if (isImageMime(mimeType) && sourceUrl) {
    return <img src={sourceUrl} alt="" className={Styles.fileRowThumb} />
  }
  if (isVideoMime(mimeType)) return <Video size={24} color="var(--dark-80)" />
  if (mimeType === 'application/pdf') return <Pdf size={24} color="var(--red-50)" />
  if (mimeType === 'text/markdown') return <Markdown size={24} color="var(--green-50)" />
  if (mimeType === 'text/plain') return <TextFile size={24} color="var(--teal-50)" />
  if (mimeType?.includes('word') || mimeType?.includes('document')) {
    return <File size={24} color="var(--blue-50)" />
  }
  return <File size={24} color="var(--dark-80)" />
}

interface Props {
  brandKitId: number
  onBack: () => void
  onNext: () => void
  hideBack?: boolean
}

export const V4BrandMaterials = ({ brandKitId, onBack, onNext, hideBack }: Props) => {
  const { openModal } = useModals()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [deletingRowIds, setDeletingRowIds] = useState<Set<string>>(new Set())

  // --- Left card: website-scraped media ---
  const websiteMediaQuery = useInfiniteBrandKitMediaFiles({
    brandKitId,
    items: 20,
    media_source: 'website',
    order_by: 'recent',
  })
  const websiteMedia = collectResults(websiteMediaQuery, (data) => data.media_files)
  const websiteImageCount = websiteMedia.filter((mf: IMediaFile) => mf.mime_type?.startsWith('image/')).length
  const websiteVideoCount = websiteMedia.filter((mf: IMediaFile) => mf.mime_type?.startsWith('video/')).length
  const websiteTotalCount = websiteMedia.length
  const hasWebsiteMedia = websiteTotalCount > 0

  const formatMediaCount = () => {
    const parts: string[] = []
    if (websiteImageCount > 0) parts.push(`${websiteImageCount} image${websiteImageCount !== 1 ? 's' : ''}`)
    if (websiteVideoCount > 0) parts.push(`${websiteVideoCount} video${websiteVideoCount !== 1 ? 's' : ''}`)
    if (parts.length === 0 && hasWebsiteMedia) return `${websiteTotalCount} file${websiteTotalCount !== 1 ? 's' : ''}`
    return parts.join(', ') || 'No media found on your site yet.'
  }

  // --- Right card: user-uploaded source materials + visual assets ---
  const documentsQuery = useBrandKitSources(
    { brandKitId, sourceType: ['SourceMaterialsMediaObject'] },
    { enabled: !!brandKitId, refetchOnWindowFocus: false },
  )
  const documents = collectResults(documentsQuery, (data) => data.brand_kit_sources) as SourceMaterialDoc[]

  const brandKitMediaQuery = useInfiniteBrandKitMediaFiles({ brandKitId, items: 50, media_source: 'brand_kit' })
  const brandKitMedia = collectResults(brandKitMediaQuery, (data) => data.media_files)

  const sourceRows: UploadedRow[] = documents.map((s) => ({
    kind: 'source',
    id: s.id,
    filename: s.name || extractFilename(s.source?.source_url),
    mimeType: s.source?.mime_type,
    sourceUrl: s.source?.source_url,
  }))

  const mediaRows: UploadedRow[] = brandKitMedia.map((mf: IMediaFile) => ({
    kind: 'media',
    id: mf.media_file_id,
    filename: mf.title ?? extractFilename(mf.source_url),
    mimeType: mf.mime_type,
    sourceUrl: mf.source_url,
    thumbnailUrl: mf.thumbnail_url,
  }))

  const uploadedRows = [...sourceRows, ...mediaRows]

  // --- Upload logic ---
  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()
  const { mutateAsync: createBrandKitSource } = useCreateBrandKitSource()
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false, undefined, undefined, {
    forceExtractBackgroundJobSync: true,
  })
  const { mutate: deleteSource } = useDeleteBrandKitSource({
    onSuccess: () => documentsQuery.refetch(),
  })

  const uploadToMediaLibrary = async (files: File[]) => {
    if (!files.length || !uploadsProjectId) return
    await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId, { brand_kit_id: brandKitId })
    await queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles'])
  }

  const uploadSourceMaterials = async (files: File[]) => {
    if (!files.length) return
    const mediaObjects = await createMediaObjects(files.map((file) => ({ file, filename: file.name })))
    await Promise.all(
      files.map((file, i) =>
        createBrandKitSource({
          brandKitId,
          sourceType: 'SourceMaterialsMediaObject',
          mediaObjectId: mediaObjects[i].id,
          name: file.name,
        }),
      ),
    )
    await documentsQuery.refetch()
  }

  const capSourceMaterialFiles = (files: File[]): File[] => {
    const availableSlots = Math.max(0, MAX_SOURCE_MATERIAL_FILES - documents.length)
    if (files.length <= availableSlots) return files

    showErrorMessageToast(
      availableSlots === 0
        ? `You can upload a maximum of ${MAX_SOURCE_MATERIAL_FILES} source material files.`
        : `Only ${availableSlots} source material file${
            availableSlots > 1 ? 's' : ''
          } can be added. Maximum ${MAX_SOURCE_MATERIAL_FILES} total.`,
    )
    return files.slice(0, availableSlots)
  }

  const handleFilesDropped = useCallback(
    async (files: File[]) => {
      if (!files.length || isUploading) return
      setIsUploading(true)

      const { mediaLibraryFiles, sourceMaterialFiles } = splitFilesByDestination(files)
      const sourceFilesToUpload = capSourceMaterialFiles(sourceMaterialFiles)
      if (!mediaLibraryFiles.length && !sourceFilesToUpload.length) {
        setIsUploading(false)
        return
      }

      const dismissToast = showToastMessage('Uploading...', 'loading', undefined, 0)
      try {
        await Promise.all([
          uploadToMediaLibrary(mediaLibraryFiles),
          sourceFilesToUpload.length ? uploadSourceMaterials(sourceFilesToUpload) : undefined,
        ])
      } catch {
        showErrorMessageToast('There was a problem uploading your files. Please try again.')
      } finally {
        dismissToast?.()
        setIsUploading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandKitId, uploadsProjectId, documents.length, isUploading],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFilesDropped,
    accept: ACCEPTED_BRAND_MATERIALS_MIME_TYPES,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const handleDeleteRow = async (row: UploadedRow) => {
    const rowKey = `${row.kind}-${row.id}`
    setDeletingRowIds((prev) => new Set(prev).add(rowKey))
    const dismissToast = showToastMessage('Deleting...', 'loading', undefined, 0)
    try {
      if (row.kind === 'source') {
        deleteSource({ brandKitId, id: row.id })
      } else {
        const response = await MediaFileClient.destroy(workspaceId, row.id)
        if (response.type !== SUCCESS_RESPONSE_TYPE) {
          showErrorMessageToast('Failed to delete file.')
          return
        }
        await queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles'])
      }
    } catch {
      showErrorMessageToast('Failed to delete file.')
    } finally {
      dismissToast?.()
      setDeletingRowIds((prev) => {
        const next = new Set(prev)
        next.delete(rowKey)
        return next
      })
    }
  }

  const handleOpenAdjustModal = () => {
    openModal(AdjustMediaModal, { brandKitId })
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length) handleFilesDropped(files)
    e.target.value = ''
  }

  return (
    <MultiStepStepLayout scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={onNext} hideBack={hideBack}>
        <div className={Styles.page}>
          <TopBar highlightStep="finishingUp" />

          <div className={Styles.content}>
            <div className={Styles.headerSection}>
              <p className={Styles.heading}>Give Blaze everything it needs to create amazing content.</p>
              <p className={Styles.subtitle}>
                This is what Blaze draws from when creating content. Upload additional materials now, or add them later.
              </p>
            </div>

            <div className={Styles.cardsRow}>
              {/* ---- Left card: From your website ---- */}
              <div className={Styles.card}>
                <div className={cx(Styles.iconBadge, Styles.iconBadgePurple)}>
                  <Globe size={16} />
                </div>
                <p className={Styles.cardTitle}>From your website</p>
                <p className={Styles.cardDescription}>
                  Images and videos pulled from your website during analysis. Blaze uses these as the foundation for
                  your visual content.
                </p>
                <hr className={Styles.divider} />
                <div className={Styles.cardFooter}>
                  <span className={Styles.cardCount}>{formatMediaCount()}</span>
                  <Button variant="secondary" size="sm" onPress={handleOpenAdjustModal} isDisabled={!hasWebsiteMedia}>
                    Adjust
                  </Button>
                </div>
                {hasWebsiteMedia && (
                  <div className={Styles.thumbnailStrip}>
                    {websiteMedia.slice(0, 7).map((mf: IMediaFile) => (
                      <img
                        key={mf.media_file_id}
                        src={mf.thumbnail_url || mf.source_url}
                        alt=""
                        className={Styles.thumbnail}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* ---- Right card: Add more source Materials ---- */}
              <div
                {...getRootProps()}
                className={cx(Styles.card, Styles.rightCard, { [Styles.cardDropzoneActive]: isDragActive })}
              >
                <input {...getInputProps()} aria-label="Upload files" />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_BRAND_MATERIALS_MIME_TYPES}
                  className={Styles.hiddenInput}
                  onChange={handleFileInputChange}
                  aria-label="Upload files"
                />
                <div className={cx(Styles.iconBadge, Styles.iconBadgeBlue, { [Styles.fadedOnDrag]: isDragActive })}>
                  <Upload5 size={16} />
                </div>
                <p className={cx(Styles.cardTitle, { [Styles.fadedOnDrag]: isDragActive })}>
                  Add more source Materials
                </p>
                <p className={cx(Styles.cardDescription, { [Styles.fadedOnDrag]: isDragActive })}>
                  Brand guidelines, reference docs, and additional context that help Blaze understand your voice and
                  generate smarter content.
                </p>
                <hr className={Styles.divider} />
                <div className={Styles.uploadedSection}>
                  <div className={Styles.rightCardFooter}>
                    {uploadedRows.length > 0 ? (
                      <span className={Styles.cardCount}>{uploadedRows.length} Uploaded</span>
                    ) : (
                      <span className={Styles.cardCountSub}>Drop files here or click to upload</span>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      frontIcon={Upload}
                      onPress={handleUploadClick}
                      isDisabled={isUploading}
                    >
                      {uploadedRows.length > 0 ? 'Upload More' : 'Upload'}
                    </Button>
                  </div>
                  {uploadedRows.length > 0 && (
                    <div className={Styles.fileList}>
                      {uploadedRows.map((row) => (
                        <FileRow
                          key={`${row.kind}-${row.id}`}
                          row={row}
                          onDelete={() => handleDeleteRow(row)}
                          isDeleting={deletingRowIds.has(`${row.kind}-${row.id}`)}
                        />
                      ))}
                    </div>
                  )}
                  {uploadedRows.length === 0 && <div className={Styles.cardSpacer} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </V4StepFooter>
    </MultiStepStepLayout>
  )
}

const FileRow = ({ row, onDelete, isDeleting }: { row: UploadedRow; onDelete: () => void; isDeleting: boolean }) => {
  const mimeType = row.mimeType
  const sourceUrl = row.kind === 'media' ? row.thumbnailUrl || row.sourceUrl : row.sourceUrl
  const label =
    row.kind === 'media' || isImageMime(mimeType) || isVideoMime(mimeType) ? 'Visual asset' : 'Source material'

  return (
    <div className={Styles.fileRow}>
      <div className={Styles.fileRowDocName}>
        <div className={Styles.fileRowIcon}>{getRowIcon(mimeType, sourceUrl)}</div>
        <Text variant="smallList" color="var(--dark-80)" lineClamp={1} className={Styles.fileRowName}>
          {row.filename}
        </Text>
      </div>
      <Text variant="smallList" color="var(--dark-60)" className={Styles.fileRowLabel}>
        {label}
      </Text>
      <IconButton
        icon={Trash2}
        variant="ghost"
        size="sm"
        onPress={onDelete}
        isDisabled={isDeleting}
        aria-label={`Delete ${row.filename}`}
      />
    </div>
  )
}
